// @ts-nocheck
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { clearAllWrittenAssessments } from '@/apps/customers/hooks/budgetAssessmentWrittenCache';
import { PlatformRequestError } from '@/apps/ingestion/services/_platformClient';
import { makeNoDebtTypeCohort, NO_DEBT_TYPE_ID } from '@/lib/debt-type-cohort';
import { resolveOperatorForSession } from '@/lib/market-organization';
import {
  fetchContextClients,
  fetchContextDebtTypes,
  fetchContextOperators,
  platformErrorMessageWithFallback,
  platformErrorStatus,
  shouldRetryBootstrapError,
} from '@/services/context';
import type { Client, DebtType, Operator } from '../types';

type LoadingState = {
  operators: boolean;
  clients: boolean;
  debtTypes: boolean;
};

type ErrorState = {
  operators: Error | null;
  clients: Error | null;
  debtTypes: Error | null;
};

interface HubContextValue {
  operators: Operator[];
  selectedOperator: Operator | null;
  setSelectedOperator: (operator: Operator) => Promise<void>;
  updateSelectedOperator: (fields: Partial<Operator>) => void;

  clients: Client[];
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null, options?: { persist?: boolean }) => void;
  /** Merges a saved edit into that client wherever it is held. Never changes scope. */
  applyClientEdit: (clientId: string, fields: Partial<Client>) => void;
  addClient: (client: Client) => void;
  /** Adds a client to the list without selecting it, for clients created outside Settings. */
  registerClient: (client: Client) => void;
  /** True when FGA grants wildcard all-clients (`*`). */
  hasAllClientsAccess: boolean;

  debtTypes: DebtType[];
  selectedDebtType: DebtType | null;
  /** `quiet` suppresses the scope toast for clears that are side effects of navigation, not a choice. */
  setSelectedDebtType: (debtType: DebtType | null, options?: { quiet?: boolean }) => void;
  addDebtType: (debtType: DebtType) => void;
  upsertDebtType: (debtType: DebtType) => void;
  removeDebtType: (debtTypeId: string) => void;

  isOperatorWideView: boolean;
  setOperatorWideView: () => void;

  /**
   * Ask the sidebar context switcher to open — for pages that are blocked on a
   * scope selection and want to point the user at the control that sets it.
   */
  requestContextSwitcher: () => void;
  /**
   * Increments on each `requestContextSwitcher()` call. The switcher owns its
   * own open state and watches this; a counter rather than a boolean so a
   * repeat request re-opens after the user dismisses the popover.
   */
  contextSwitcherRequest: number;

  /**
   * Re-run the operator bootstrap after a transient failure. The bootstrap
   * effect has no other state change that would retrigger it, so without this
   * a terminal error strands the session with no operator context.
   */
  retryBootstrap: () => void;

  isLoading: boolean;
  loadingState: LoadingState;
  errorState: ErrorState;
  /** True once the clients fetch has completed for the current operator at least once. */
  clientsSettled: boolean;
}

/**
 * Say which scope is now in effect.
 *
 * Scope decides what every page reads and writes, so switching it silently is
 * how someone edits the wrong client's configuration without noticing. One
 * toast id means a switch replaces the last announcement rather than stacking,
 * and a client switch that also clears a debt type reads as one change.
 */
const SCOPE_TOAST_ID = 'hub-scope-switch';
/** Long enough for the spinner to register, short enough not to feel like a wait. */
const SCOPE_SETTLE_MS = 450;

let scopeSettleTimer: ReturnType<typeof setTimeout> | undefined;

function announceScope(name: string) {
  // A switch made before the previous one settled replaces it outright, so the
  // confirmation can never name a scope that is no longer in effect.
  clearTimeout(scopeSettleTimer);
  // The switch itself is synchronous, but it reprices every query on the page.
  // Showing the spinner first and resolving it in place makes that visible as
  // one action settling rather than a result appearing from nowhere.
  // The spinner stays neutral so the green arriving *is* the "done" signal.
  toast.loading(`Switching scope...`, { id: SCOPE_TOAST_ID });
  scopeSettleTimer = setTimeout(() => {
    // Scoped to this toast rather than the shared Toaster, which every success
    // in the Hub goes through.
    toast.success(`Switched to ${name}`, {
      id: SCOPE_TOAST_ID,
      // The same green the success Badge uses, via highlight-green; there is no
      // `success` colour token, so the palette classes are the honest route.
      classNames: { icon: 'text-green-600 dark:text-green-500' },
    });
  }, SCOPE_SETTLE_MS);
}

const HubContext = createContext<HubContextValue | undefined>(undefined);

const CLIENT_STORAGE_KEY = 'currentClientId';
const DEBT_TYPE_STORAGE_KEY = 'currentDebtTypeId';

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

async function retryOnError<T>(fn: () => Promise<T>, attempts = 2): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= attempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt === attempts || !shouldRetryBootstrapError(error)) {
        throw error;
      }
      await sleep(500 * 2 ** attempt);
    }
  }
  throw lastError;
}

interface HubProviderProps {
  children: ReactNode;
}

export function HubProvider({ children }: HubProviderProps) {
  const { switchToOrganization, organizationId, isLoading: authLoading, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Data state
  const [operators, setOperators] = useState<Operator[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [hasAllClientsAccess, setHasAllClientsAccess] = useState(false);
  const [clientsSettled, setClientsSettled] = useState(false);
  const [allDebtTypes, setAllDebtTypes] = useState<DebtType[]>([]);

  // Selection state
  const [selectedOperator, setSelectedOperatorState] = useState<Operator | null>(null);
  const [selectedClient, setSelectedClientState] = useState<Client | null>(null);
  const [selectedDebtType, setSelectedDebtTypeState] = useState<DebtType | null>(null);

  // Loading and error state
  const [loadingState, setLoadingState] = useState<LoadingState>({
    operators: true,
    clients: false,
    debtTypes: false,
  });
  const [errorState, setErrorState] = useState<ErrorState>({
    operators: null,
    clients: null,
    debtTypes: null,
  });

  // Track previous organizationId to detect operator switches
  const previousOrganizationIdRef = useRef<string | null>(null);
  const lastBootstrapAttemptRef = useRef(0);

  // Clean up legacy localStorage key on first load
  useEffect(() => {
    localStorage.removeItem('currentPortfolioId');
  }, []);

  // Computed state - clients filtered to selected operator
  const clients = useMemo(() => {
    if (!selectedOperator) return [];
    return allClients.filter((c) => c.operator_id === selectedOperator.id);
  }, [selectedOperator, allClients]);

  // Debt types filtered to selected client (debt types sit BELOW client in hierarchy)
  const debtTypes = useMemo(() => {
    if (!selectedClient) return [];
    return allDebtTypes.filter((dt) => dt.client_id === selectedClient.id);
  }, [selectedClient, allDebtTypes]);

  const isOperatorWideView = useMemo(() => selectedClient === null, [selectedClient]);

  const [contextSwitcherRequest, setContextSwitcherRequest] = useState(0);
  const requestContextSwitcher = useCallback(() => setContextSwitcherRequest((n) => n + 1), []);

  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const retryBootstrap = useCallback(() => setBootstrapAttempt((n) => n + 1), []);

  const isLoading = useMemo(
    () => loadingState.operators || loadingState.clients || loadingState.debtTypes || authLoading,
    [loadingState.operators, loadingState.clients, loadingState.debtTypes, authLoading]
  );

  // Effect 1: Fetch operators when auth is ready and organizationId is available
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (!organizationId) return;

    if (previousOrganizationIdRef.current === organizationId && bootstrapAttempt === lastBootstrapAttemptRef.current) {
      if (operators.length > 0 && selectedOperator) return;
    }
    lastBootstrapAttemptRef.current = bootstrapAttempt;

    const isOperatorSwitch = previousOrganizationIdRef.current !== null && previousOrganizationIdRef.current !== organizationId;
    previousOrganizationIdRef.current = organizationId;

    if (isOperatorSwitch) {
      // Clear context immediately so stale client state cannot leak into the next operator load.
      setAllClients([]);
      setHasAllClientsAccess(false);
      setClientsSettled(false);
      setAllDebtTypes([]);
      setSelectedClientState(null);
      setSelectedDebtTypeState(null);
    }

    let isCancelled = false;

    async function fetchOperators() {
      try {
        setErrorState((prev) => ({ ...prev, operators: null }));
        setLoadingState((prev) => ({ ...prev, operators: true }));

        await new Promise((resolve) => setTimeout(resolve, 50));

        const operatorsData = await retryOnError(() => fetchContextOperators(), 3);

        if (isCancelled) return;

        const operatorToSelect = resolveOperatorForSession(operatorsData, organizationId);

        setOperators(operatorsData);
        setLoadingState((prev) => ({ ...prev, operators: false }));
        setSelectedOperatorState(operatorToSelect);
      } catch (error) {
        if (isCancelled) return;
        setLoadingState((prev) => ({ ...prev, operators: false }));
        if (operators.length > 0 && selectedOperator) {
          console.warn('Operator refresh failed; keeping the current workspace', error);
          return;
        }
        const err = new PlatformRequestError(
          platformErrorMessageWithFallback(error, 'Failed to fetch operators'),
          platformErrorStatus(error)
        );
        console.error('Error fetching operators:', error);
        setErrorState((prev) => ({ ...prev, operators: err }));
      }
    }

    fetchOperators();

    return () => {
      isCancelled = true;
    };
  }, [organizationId, authLoading, isAuthenticated, operators.length, selectedOperator, bootstrapAttempt]);

  // Effect 1.5: Select operator if operators exist but no operator is selected
  useEffect(() => {
    if (operators.length > 0 && !selectedOperator && organizationId && !authLoading && isAuthenticated) {
      const operatorToSelect = resolveOperatorForSession(operators, organizationId);
      if (operatorToSelect) {
        setSelectedOperatorState(operatorToSelect);
      }
    }
  }, [operators, selectedOperator, organizationId, authLoading, isAuthenticated]);

  // Effect 2a: Clear clients and debt types when operator is deselected
  useEffect(() => {
    if (!selectedOperator) {
      setAllClients([]);
      setHasAllClientsAccess(false);
      setAllDebtTypes([]);
      setSelectedClientState(null);
      setSelectedDebtTypeState(null);
    }
  }, [selectedOperator]);

  // Effect 2b: Fetch clients after operator is selected
  useEffect(() => {
    if (!selectedOperator) return;
    if (loadingState.operators || authLoading) return;

    const currentOperator = selectedOperator;
    let isCancelled = false;

    async function fetchClients() {
      try {
        setErrorState((prev) => ({ ...prev, clients: null }));
        setLoadingState((prev) => ({ ...prev, clients: true }));

        const { clients, allClients: allClientsAccess } = await retryOnError(() => fetchContextClients());

        if (isCancelled) return;

        setAllClients(clients);
        setHasAllClientsAccess(allClientsAccess);
        setClientsSettled(true);
        setLoadingState((prev) => ({ ...prev, clients: false }));

        // Restore saved client selection
        const savedClientId = localStorage.getItem(CLIENT_STORAGE_KEY);
        const savedClient =
          savedClientId && savedClientId !== 'operator-wide'
            ? clients.find((c) => c.id === savedClientId && c.operator_id === currentOperator.id)
            : null;

        setSelectedClientState(savedClient ?? null);
      } catch (error) {
        if (isCancelled) return;
        const err = new Error(platformErrorMessageWithFallback(error, 'Failed to fetch clients'));
        console.error('Error fetching clients:', error);
        // Keep prior hasAllClientsAccess — a fetch outage must not look like
        // confirmed lack of access (would redirect authorized users off bank/PSP).
        setErrorState((prev) => ({ ...prev, clients: err }));
        setLoadingState((prev) => ({ ...prev, clients: false }));
      }
    }

    fetchClients();

    return () => {
      isCancelled = true;
    };
  }, [selectedOperator, loadingState.operators, authLoading]);

  // Effect 3: Fetch debt types after client is selected (debt types are scoped to client)
  useEffect(() => {
    if (!selectedClient) {
      setAllDebtTypes([]);
      setSelectedDebtTypeState(null);
      return;
    }
    if (loadingState.clients) return;

    const currentClient = selectedClient;
    let isCancelled = false;

    async function fetchDebtTypes() {
      try {
        setErrorState((prev) => ({ ...prev, debtTypes: null }));
        setLoadingState((prev) => ({ ...prev, debtTypes: true }));

        const debtTypes = await fetchContextDebtTypes(currentClient.id);

        if (isCancelled) return;

        setAllDebtTypes(debtTypes);
        setLoadingState((prev) => ({ ...prev, debtTypes: false }));

        // Restore saved debt type selection. The synthetic "Others" cohort has no
        // row in `debtTypes`, so restore it from the sentinel directly.
        const savedDebtTypeId = localStorage.getItem(DEBT_TYPE_STORAGE_KEY);
        if (savedDebtTypeId === NO_DEBT_TYPE_ID) {
          setSelectedDebtTypeState(makeNoDebtTypeCohort(currentClient));
        } else {
          const savedDebtType = savedDebtTypeId ? debtTypes.find((dt) => dt.id === savedDebtTypeId) : undefined;
          if (savedDebtType) setSelectedDebtTypeState(savedDebtType);
        }
      } catch (error) {
        if (isCancelled) return;
        const err = new Error(platformErrorMessageWithFallback(error, 'Failed to fetch debt types'));
        console.error('Error fetching debt types:', error);
        setErrorState((prev) => ({ ...prev, debtTypes: err }));
        setLoadingState((prev) => ({ ...prev, debtTypes: false }));
      }
    }

    fetchDebtTypes();

    return () => {
      isCancelled = true;
    };
  }, [selectedClient, loadingState.clients]);

  // Current scope as the setters see it, kept in step synchronously so two
  // setters called in one tick agree on what changed.
  const scopeRef = useRef({ client: selectedClient, debtType: selectedDebtType });
  scopeRef.current = { client: selectedClient, debtType: selectedDebtType };

  // Action handlers
  const setSelectedOperator = useCallback(
    async (operator: Operator) => {
      if (operator.workos_organization_id) {
        await switchToOrganization({ organizationId: operator.workos_organization_id });
      }

      // JWT/FGA scope changed — cancel in-flight requests, then clear cached data.
      // Clear cache BEFORE updating operator state to prevent rendering with
      // new operator + old cached data (cross-operator data flash).
      await queryClient.cancelQueries();
      queryClient.clear();
      // sessionStorage is outside the query cache, so the clear above does not reach it.
      // Keyed on the WorkOS person, which a switchToOrganization does not change.
      clearAllWrittenAssessments();

      // Now safe to update state — no stale data to render
      setSelectedOperatorState(operator);
      setSelectedClientState(null);
      setSelectedDebtTypeState(null);
      scopeRef.current = { client: null, debtType: null };
      localStorage.removeItem(CLIENT_STORAGE_KEY);
      localStorage.removeItem(DEBT_TYPE_STORAGE_KEY);
      announceScope(operator.name);
    },
    [switchToOrganization, queryClient]
  );

  const setSelectedClient = useCallback((client: Client | null, options?: { persist?: boolean }) => {
    const persist = options?.persist !== false;
    setSelectedClientState(client);
    // Clear debt type when changing client (debt type is a refinement within client)
    setSelectedDebtTypeState(null);
    // Saved now, not at the next render, so a setSelectedDebtType(null) issued in
    // the same tick sees the debt type already gone and does not re-announce.
    scopeRef.current = { client, debtType: null };
    // Scope decides what a page reads and writes, so a silent change is a
    // change nobody notices until it has already applied to the wrong thing.
    // Bootstrap and URL restore set the state directly and stay quiet.
    announceScope(client ? client.display_name : 'All Clients');
    if (!persist) return;
    if (client) {
      localStorage.setItem(CLIENT_STORAGE_KEY, client.id);
    } else {
      localStorage.removeItem(CLIENT_STORAGE_KEY);
    }
    localStorage.removeItem(DEBT_TYPE_STORAGE_KEY);
  }, []);

  const setSelectedDebtType = useCallback((debtType: DebtType | null, options?: { quiet?: boolean }) => {
    const { client, debtType: previous } = scopeRef.current;
    setSelectedDebtTypeState(debtType);
    scopeRef.current = { client, debtType };
    if (debtType) {
      announceScope(debtType.name);
    } else if (previous && client && !options?.quiet) {
      // Dropping a debt type widens the scope back to its client, which is as much a switch as narrowing was.
      announceScope(client.display_name);
    }
    if (debtType) {
      localStorage.setItem(DEBT_TYPE_STORAGE_KEY, debtType.id);
    } else {
      localStorage.removeItem(DEBT_TYPE_STORAGE_KEY);
    }
  }, []);

  const setOperatorWideView = useCallback(() => {
    setSelectedClientState(null);
    setSelectedDebtTypeState(null);
    scopeRef.current = { client: null, debtType: null };
    localStorage.removeItem(CLIENT_STORAGE_KEY);
    localStorage.removeItem(DEBT_TYPE_STORAGE_KEY);
    announceScope('All Clients');
  }, []);

  const addClient = useCallback((client: Client) => {
    setAllClients((prev) => [...prev, client]);
    setSelectedClientState(client);
    localStorage.setItem(CLIENT_STORAGE_KEY, client.id);
    setSelectedDebtTypeState(null);
    localStorage.removeItem(DEBT_TYPE_STORAGE_KEY);
  }, []);

  const registerClient = useCallback((client: Client) => {
    setAllClients((prev) => (prev.some((c) => c.id === client.id) ? prev : [...prev, client]));
  }, []);

  const addDebtType = useCallback((debtType: DebtType) => {
    setAllDebtTypes((prev) => {
      if (prev.some((dt) => dt.id === debtType.id)) return prev;
      return [...prev, debtType];
    });
  }, []);

  const upsertDebtType = useCallback((debtType: DebtType) => {
    setAllDebtTypes((prev) => {
      const idx = prev.findIndex((dt) => dt.id === debtType.id);
      if (idx === -1) return [...prev, debtType];
      const next = [...prev];
      next[idx] = debtType;
      return next;
    });
    setSelectedDebtTypeState((current) => {
      if (current?.id !== debtType.id) return current;
      if (debtType.status === 'archived') {
        localStorage.removeItem(DEBT_TYPE_STORAGE_KEY);
        return null;
      }
      return debtType;
    });
  }, []);

  const removeDebtType = useCallback((debtTypeId: string) => {
    setAllDebtTypes((prev) => prev.filter((dt) => dt.id !== debtTypeId));
    setSelectedDebtTypeState((current) => {
      if (current?.id !== debtTypeId) return current;
      localStorage.removeItem(DEBT_TYPE_STORAGE_KEY);
      return null;
    });
  }, []);

  const updateSelectedOperator = useCallback(
    (fields: Partial<Operator>) => {
      if (!selectedOperator) return;
      const updated = { ...selectedOperator, ...fields } as Operator;
      setSelectedOperatorState(updated);
      setOperators((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    },
    [selectedOperator]
  );

  // Saves resolve after the user may have moved on, and several can be in flight
  // at once, so merge into the current state rather than a snapshot.
  const applyClientEdit = useCallback((clientId: string, fields: Partial<Client>) => {
    setSelectedClientState((current) => (current?.id === clientId ? { ...current, ...fields } : current));
    setAllClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, ...fields } : c)));
  }, []);

  const contextValue = useMemo<HubContextValue>(
    () => ({
      operators,
      selectedOperator,
      setSelectedOperator,
      updateSelectedOperator,
      clients,
      selectedClient,
      setSelectedClient,
      applyClientEdit,
      addClient,
      registerClient,
      hasAllClientsAccess,
      debtTypes,
      selectedDebtType,
      setSelectedDebtType,
      addDebtType,
      upsertDebtType,
      removeDebtType,
      isOperatorWideView,
      setOperatorWideView,
      requestContextSwitcher,
      contextSwitcherRequest,
      retryBootstrap,
      isLoading,
      loadingState,
      errorState,
      clientsSettled,
    }),
    [
      operators,
      selectedOperator,
      setSelectedOperator,
      updateSelectedOperator,
      clients,
      selectedClient,
      setSelectedClient,
      applyClientEdit,
      addClient,
      registerClient,
      hasAllClientsAccess,
      debtTypes,
      selectedDebtType,
      setSelectedDebtType,
      addDebtType,
      upsertDebtType,
      removeDebtType,
      isOperatorWideView,
      setOperatorWideView,
      requestContextSwitcher,
      contextSwitcherRequest,
      retryBootstrap,
      isLoading,
      loadingState,
      errorState,
      clientsSettled,
    ]
  );

  return <HubContext.Provider value={contextValue}>{children}</HubContext.Provider>;
}

export function useHub() {
  const context = useContext(HubContext);
  if (context === undefined) {
    throw new Error('useHub must be used within a HubProvider');
  }
  return context;
}
